import { ISelectionMoveBridge, ISelectionMoveStart } from "@kuika/kuika-cl-model";
import React, { useEffect, useRef } from "react";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { getSelectionMoveBridge } from "./canvas-frame";

export interface ISelectionDragGripProps {
  /**
   * Told when a grip drag starts and ends, so the overlay keeps the grip (the drag's source) mounted.
   * Called from a timer after `dragstart`, so it must be stable (a state setter).
   */
  onDraggingChange: (isDragging: boolean) => void;
  /** Injectable for tests; defaults to the canvas agent's `window.kuika.selectionMoveBridge`. */
  resolveBridge?: () => ISelectionMoveBridge | null;
}

/** A 1×1 transparent image: an absolutely positioned element follows the pointer itself, so no ghost. */
let emptyDragImage: HTMLImageElement | null = null;
const getEmptyDragImage = (): HTMLImageElement => {
  if (emptyDragImage === null) {
    emptyDragImage = new Image(1, 1);
    emptyDragImage.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
  }
  return emptyDragImage;
};

/** How long the drag preview stays in the document: the browser snapshots it during `dragstart`. */
const PREVIEW_LIFETIME_MS: number = 100;

/**
 * `dataTransfer` keys carrying this drag's start pointer position, in the designer page's own
 * coordinates. `ISelectionMoveBridge.begin` takes only a `DataTransfer` (kuika-cl-model, shared by
 * both packages), so there is no typed parameter to add the pointer to without a cross-package
 * release; it rides the same synchronous `DataTransfer` channel `begin` already uses for
 * `kuikaid`/`reason` instead. The agent needs this because `begin` runs inside the canvas iframe,
 * a different coordinate space, and cannot otherwise learn where the pointer grabbed the grip — see
 * `resolveGripPointer` in `agents/kuika-react-cl-agent/src/react-helper.tsx`, which must keep these
 * two key strings in sync by hand.
 */
const GRIP_POINTER_CLIENT_X_KEY = "kuikaSelectionMoveClientX";
const GRIP_POINTER_CLIENT_Y_KEY = "kuikaSelectionMoveClientY";

/** A flow design's drag image: the moved component's name in a pill styled like the bar. */
const setNamedDragImage = (dataTransfer: DataTransfer, componentName: string): void => {
  const preview: HTMLDivElement = document.createElement("div");
  preview.className = "kuika-selection-drag-preview";
  preview.textContent = componentName;
  document.body.appendChild(preview);
  dataTransfer.setDragImage(preview, 0, 0);
  window.setTimeout((): void => preview.remove(), PREVIEW_LIFETIME_MS);
};

/**
 * The selection bar's move handle. The drop targets live in the canvas iframe, so the grip starts the
 * canvas agent's own move-existing-item drag through `ISelectionMoveBridge`: `begin` (synchronous, the
 * only moment `DataTransfer` is writable) sets `movingSourceElement` and the drag data the canvas `drop`
 * reads, and prepares an absolutely positioned (report) design to follow the pointer; `end` runs from
 * this element's `dragend`, which fires in the designer document and never reaches the canvas.
 *
 * A `span`, not a `button`: Firefox does not start a drag from a button. It is not focusable — a drag has
 * no keyboard equivalent here; Move up / Move down in the ⋯ menu are the keyboard path.
 */
export const SelectionDragGrip = ({
  onDraggingChange,
  resolveBridge = getSelectionMoveBridge
}: ISelectionDragGripProps): JSX.Element => {
  const activeBridgeRef: React.MutableRefObject<ISelectionMoveBridge | null> = useRef<ISelectionMoveBridge | null>(
    null
  );

  // Backstop: were the grip unmounted mid-drag its `dragend` would never arrive, leaving the canvas in
  // its dragging state (outline parked, the overlay suppressed).
  useEffect(
    (): (() => void) => (): void => {
      activeBridgeRef.current?.end();
      activeBridgeRef.current = null;
    },
    []
  );

  const handleDragStart = (event: React.DragEvent<HTMLSpanElement>): void => {
    const bridge: ISelectionMoveBridge | null = resolveBridge();
    // Write the start pointer before `begin`: on an absolute (report) design the agent anchors the
    // move to this grab point (`beginPointerTrackedMove`) instead of snapping the element's top-left
    // under the cursor, and `begin` reads it back off the same `dataTransfer`.
    event.dataTransfer.setData(GRIP_POINTER_CLIENT_X_KEY, String(event.clientX));
    event.dataTransfer.setData(GRIP_POINTER_CLIENT_Y_KEY, String(event.clientY));
    const start: ISelectionMoveStart | null = bridge !== null ? bridge.begin(event.dataTransfer) : null;
    if (bridge === null || start === null) {
      event.preventDefault();
      return;
    }
    activeBridgeRef.current = bridge;
    if (start.isAbsoluteDesign) {
      event.dataTransfer.setDragImage(getEmptyDragImage(), 0, 0);
    } else {
      setNamedDragImage(event.dataTransfer, start.componentName);
    }
    // Deferred: a re-render inside `dragstart` that touches the source can make Chrome abort the drag.
    window.setTimeout((): void => {
      if (activeBridgeRef.current === bridge) {
        onDraggingChange(true);
      }
    }, 0);
  };

  const handleDragEnd = (): void => {
    activeBridgeRef.current?.end();
    activeBridgeRef.current = null;
    onDraggingChange(false);
  };

  return (
    <KuikaTooltip content="Drag to move" position="top" bgcolor="inverse">
      <span
        className="kuika-selection-bar__grip"
        draggable
        role="img"
        aria-label="Drag to move"
        data-testid="canvasSelectionHandle"
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <KuikaSvg name="grip-vertical" width={16} height={16} color="currentColor" aria-hidden="true" />
      </span>
    </KuikaTooltip>
  );
};
